//omponents/SchedulingEmployer/ExistingScheduleSection.js
import React, { useMemo } from "react";
import Select from "react-select";
import { DateTime } from "luxon";

import {
  CalendarDays,
  Pencil,
  Trash2,
  Repeat2,
  UserRound,
  Search,
} from "lucide-react";

const SELECT_Z = 99999;
const selectStyles = {
  menuPortal: (base) => ({ ...base, zIndex: SELECT_Z }),
  menu: (base) => ({ ...base, zIndex: SELECT_Z }),
};

const formatScheduleDate = (iso) => {
  if (!iso) return "";

  return DateTime.fromISO(iso).toFormat("MMM d, yyyy");
};

export default function ExistingScheduleSection({
  employees,
  selectedEmployee,
  setSelectedEmployee,
  existingSchedules,
  handleEditSchedule,
  handleDeleteSchedule,
  isHistoryLoading = false,
  deletingId = null,
  Loader,
  companyTimeZone = "UTC", // ✅ add
}) {
  const todayISO = useMemo(
    () =>
      DateTime.now()
        .setZone(companyTimeZone)
        .toISODate(),
    [companyTimeZone],
  );

  const getScheduleStatus = (startISO, endISO) => {
    if (!startISO || !endISO) return { label: "", key: "" };

    // inclusive:
    // - Current: start <= today <= end
    // - Upcoming: today < start
    // - Past: today > end
    if (todayISO < startISO) return { label: "Upcoming", key: "upcoming" };
    if (todayISO > endISO) return { label: "Past", key: "past" };
    return { label: "Current", key: "current" };
  };

  return (
    <div className="schedule-card-container schedule-history-container">
      {/* FILTER */}
      <div className="schedule-history-filter">
        <div className="schedule-history-filter-heading">
          <div>
            <div className="schedule-history-filter-title">
              Filter schedules
            </div>

            <div className="schedule-history-filter-sub">
              Find schedules by employee.
            </div>
          </div>

          <div className="schedule-history-count">
            {existingSchedules.length}{" "}
            {existingSchedules.length === 1 ? "schedule" : "schedules"}
          </div>
        </div>

        <div className="schedule-history-select-wrap">
          <Search size={17} className="schedule-history-search-icon" />

          <div className="schedule-history-select">
            <Select
              options={employees.map((emp) => ({
                value: emp.id,
                label: `${emp.name} (${emp.role || "Employee"})`,
              }))}
              onChange={(opt) => setSelectedEmployee(opt?.value || "")}
              value={
                selectedEmployee
                  ? {
                      value: selectedEmployee,
                      label:
                        employees.find((e) => e.id === selectedEmployee)
                          ?.name || "",
                    }
                  : null
              }
              isClearable
              placeholder="Search or select an employee..."
              menuPortalTarget={
                typeof document !== "undefined" ? document.body : null
              }
              menuPosition="fixed"
              menuShouldScrollIntoView={false}
              styles={selectStyles}
            />
          </div>
        </div>
      </div>

      {/* SCHEDULE LIST */}
      <div className="schedule-history-list">
        {isHistoryLoading ? (
          Loader ? (
            <Loader label="Loading schedules..." />
          ) : (
            <div className="schedule-history-empty">Loading schedules...</div>
          )
        ) : existingSchedules.length > 0 ? (
          existingSchedules.map((s) => {
            const st = getScheduleStatus(s.weekStartDate, s.weekEndDate);

            const initial =
              (s.employeeName || "?")
                .trim()
                .charAt(0)
                .toUpperCase() || "?";

            return (
              <div className="schedule-history-card" key={s.id}>
                <div className="schedule-history-card-main">
                  {/* EMPLOYEE */}
                  <div className="schedule-history-employee">
                    <div className="schedule-history-avatar">{initial}</div>

                    <div className="schedule-history-employee-info">
                      <div className="schedule-history-name">
                        {s.employeeName}
                      </div>

                      <div className="schedule-history-date">
                        <CalendarDays size={15} />

                        <span>
                          {formatScheduleDate(s.weekStartDate)}
                          {" – "}
                          {formatScheduleDate(s.weekEndDate)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* STATUS */}
                  <div className="schedule-history-meta">
                    {st.label && (
                      <span className={`schedule-history-status ${st.key}`}>
                        {st.label}
                      </span>
                    )}

                    {s.repeatWeekly && (
                      <span className="schedule-history-recurring">
                        <Repeat2 size={14} />
                        Repeats weekly
                      </span>
                    )}
                  </div>
                </div>

                {/* ACTIONS */}
                <div className="schedule-history-card-footer">
                  <div className="schedule-history-week-label">
                    <UserRound size={15} />
                    Employee schedule
                  </div>

                  <div className="schedule-history-actions">
                    <button
                      className="schedule-history-edit-btn"
                      type="button"
                      onClick={() => handleEditSchedule(s)}
                    >
                      <Pencil size={16} />
                      <span>Edit</span>
                    </button>

                    <button
                      className="schedule-history-delete-btn"
                      type="button"
                      onClick={() => handleDeleteSchedule(s.id)}
                      disabled={deletingId === s.id}
                    >
                      <Trash2 size={16} />

                      <span>
                        {deletingId === s.id ? "Deleting..." : "Delete"}
                      </span>
                    </button>
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <div className="schedule-history-empty">
            <div className="schedule-history-empty-icon">
              <CalendarDays size={24} />
            </div>

            <div className="schedule-history-empty-title">
              No schedules found
            </div>

            <div className="schedule-history-empty-sub">
              No schedules were found for the selected employee.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
